import type { ReactNode } from 'react';
import { Loader, Stack, Text } from '@cowbell/ui';

export interface PanelBodyStateArgs<TProfile> {
  readonly isNotFound: boolean;
  readonly isError: boolean;
  readonly profile: TProfile | null;
  readonly isLoading: boolean;
  readonly notFoundMessage: string;
  readonly errorMessage: string;
  readonly loadingMessage: string;
  readonly refetch: () => void;
  readonly styles: Readonly<Record<string, string>>;
  readonly renderDetail: (profile: TProfile) => ReactNode;
}

/**
 * Shared body state-gating for the citation slide-out panels (ThreatActorPanel,
 * CvePanel): not-found / error+Retry / seeded-or-loaded profile / loading —
 * every panel's body reduces to the same four states, differing only in
 * copy and which detail component renders the loaded profile. Extracted
 * after both panels independently grew a byte-for-byte identical
 * `renderBody` (Sonar CPD flagged the duplication).
 *
 * isError takes priority over a seeded profile: even when the profile hook
 * returns a seed-derived value, a fetch failure must surface the error
 * message and Retry button rather than a sparse seeded body.
 */
export function renderPanelBody<TProfile>({
  isNotFound,
  isError,
  profile,
  isLoading,
  notFoundMessage,
  errorMessage,
  loadingMessage,
  refetch,
  styles,
  renderDetail,
}: PanelBodyStateArgs<TProfile>): ReactNode {
  if (isNotFound) {
    return (
      <Stack align="center" justify="center" h="100%">
        <Text size="sm" c="dimmed">
          {notFoundMessage}
        </Text>
      </Stack>
    );
  }
  if (isError) {
    return (
      <Stack align="center" justify="center" h="100%">
        <Text size="sm" c="red">
          {errorMessage}
        </Text>
        <button
          className={styles.retryBtn}
          onClick={() => refetch()}
          type="button"
        >
          Retry
        </button>
      </Stack>
    );
  }
  if (profile) {
    return (
      <>
        {renderDetail(profile)}
        {isLoading && (
          <Stack align="center" mt="md">
            <Loader size="sm" />
          </Stack>
        )}
      </>
    );
  }
  return (
    <Stack align="center" justify="center" h="100%">
      <Loader size="md" />
      <Text size="sm" c="dimmed">
        {loadingMessage}
      </Text>
    </Stack>
  );
}
